import React from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {Location} from '@reach/router';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Footer from 'components/Footer';
import {OptinModalTrigger} from 'components/OptinModal';
import OptinFormChallenge from 'components/OptinFormChallenge/OptinFormChallenge';
import OptinFormChallengeModal from 'components/OptinFormChallenge/OptinFormChallengeModal';
import CountdownTimer from 'components/Elements/CountdownTimer';
import ScrollingVideo from 'components/ComponentLibrary/ScrollingVideo';
import FaqListCL from 'components/ComponentLibrary/FaqListCL/FaqListCL';
import ImageCardCL from 'components/ComponentLibrary/ImageCardCL';
import ReactMarkdown from 'react-markdown';

//@ts-ignore
import {guests, cohosts, faqs} from 'data/wlc-lc.json';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import ExitModal from 'components/ComponentLibrary/ExitModal';
import FollowButtons from 'components/Social/FollowButtons';
const {SF_API_URL} = process.env;
const {ORDER_API_URL} = process.env;
const {SITE_ID} = process.env;
const {LIVE_CHALLENGE_OPTIN_FORM_ID} = process.env;
const {LIVE_CHALLENGE_ORDER_OFFER_ID} = process.env;
const {LIVE_CHALLENGE_ORDER_OFFER_PAYMENT_OPTION_ID} = process.env;

export default function (props) {
  return <Location>{({location}) => <ChallengeLanding location={location} {...props} />}</Location>;
}

function ChallengeLanding({
  entry = 'default',
  variant = 'default',
  noExitIntent = false,
  noFixedVideo = false,
  optinOverride = false,
  pageTitle = 'Join the free Eat The Rainbow Challenge!',
  pageDescription = 'Join thousands of individuals saying YES to adding more color to their plates for increased nutrients.',
  ogUrl = 'https://wlc.foodrevolution.org/challenge/',
  ogImage = 'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/wlc/challenge/og/wlc-lc-202504-open-graph-challenge.png',
  location = {},
  ...props
}) {
  const {
    phases: {
      challenge: challengeSchedule = {
        phase: 'before',
        salesEndDate: ''
      }
    }
  } = usePhaseState();
  const phase = challengeSchedule.phase;
  const registrationDate = challengeSchedule.challengeRegistrationEnd;
  const registrationTime = new Date(registrationDate).getTime();
  const wlcCustomer = entry === 'wlcCustomer';
  const optinNavigateUrl = wlcCustomer ? '/challenge/wlc/welcome/' : '/challenge/welcome/';
  const noRegistration = phase === 'after' && !optinOverride && !challengeSchedule.keepRegistrationOpen;

  const headerImageUrl = 'https://cdn.foodrevolution.org/eattherainbow/images/1129818010-lg.jpg';
  const headerImageAlt = 'colorful vegetables and fruits in rainbow colors';

  function Timer({headlineClass = undefined, className = undefined}) {
    return (
      <div className={classNames(`timer-wrap`, className)}>
        <h5 className={classNames(`text-uppercase`, `mt-0`, `mb-2`, headlineClass)}>Registration Closes In:</h5>
        <CountdownTimer time={registrationTime} />
      </div>
    );
  }

  function OptinTriggerContent() {
    return (
      <>
        <OptinModalTrigger btnText="Register Now!" disabled={noRegistration ? true : false} />
        {phase === 'during' && !optinOverride ? <Timer className="my-4" /> : null}
        {noRegistration && (
          <p className="text-small mt-1">
            <i>Registration for the challenge has closed.</i>
          </p>
        )}
        <p className="mt-3">May 16–20, at 12:00 PM ET / 9:00 AM PT</p>
        {entry === 'default' && (
          <p className="balance-text text-small mt-4">
            <i>
              The event is TOTALLY FREE. And if you want to go further — you’ll get an invitation to join FRN’s thriving
              membership community, WHOLE Life Club, with a $1 trial and a special discount.
            </i>
          </p>
        )}
      </>
    );
  }

  useBalanceText();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} ogImage={ogImage} url={ogUrl} />
      <div className="page-content d-flex flex-column flex-grow-1">
        <SectionCL
          id="landing-header"
          color="black"
          className="sectionCL-background-image-fixed"
          imgUrl={headerImageUrl}
          imgDescription={headerImageAlt}
          angle="none"
          order="first"
          innerClass="pt-0"
        >
          <div className="header-cta-box background-black-80 py-4 text-center text-white">
            <Container>
              <Row>
                <Col>
                  <p className="mb-2">
                    <i>Free to all!</i>
                  </p>
                  {phase === 'during' && !optinOverride ? <Timer /> : null}
                </Col>
              </Row>
            </Container>
          </div>
          <Container className="header-cta-optin">
            <Row className="justify-content-center">
              <Col xs="12" lg="10">
                <div className="optin-box background-light-gray box-shadow-black-50 p-4 rounded">
                  <div className="text-center mb-3">
                    <img
                      className="mb-3"
                      src="https://cdn.foodrevolution.org/wlc/challenge/wlc-logo-challenge-color.svg"
                      alt="whole life club challenge logo"
                      style={{maxHeight: '150px'}}
                    />
                    <h1 className="text-h2 mt-0 mb-2">Eat the Rainbow Challenge</h1>
                    <h5 className="mt-0 mb-2">
                      A Free 5-Day Adventure to Pack Your Plate with Colorful Plant-Based Foods
                    </h5>
                    <p className="mb-1">May 16 – May 20, 2025</p>
                    <p className="mb-0">at 12:00 PM ET / 9:00 AM PT</p>
                  </div>
                  {noRegistration ? (
                    <div className="background-black-10 text-center p-4 rounded">
                      <p className="m-0">
                        <i>Registration for the challenge has closed.</i>
                      </p>
                    </div>
                  ) : (
                    <OptinFormChallenge
                      apiUrl={SF_API_URL}
                      orderApiUrl={ORDER_API_URL}
                      siteId={SITE_ID}
                      contactInfoFormHeader={
                        <div className="text-center">
                          <p className={classNames(entry === 'wlcCustomer' && 'mb-2')}>
                            Enter your email below to join the <b>Eat the Rainbow Challenge.</b>
                          </p>
                          {entry === 'wlcCustomer' && (
                            <p className="balance-text text-small">
                              <b>Note:</b> Please use the same email that you use to log into WHOLE Life Club. If you
                              don’t use the same email, a new account with new login credentials will be created for
                              you.
                            </p>
                          )}
                        </div>
                      }
                      entry={entry}
                      optinFormId={LIVE_CHALLENGE_OPTIN_FORM_ID}
                      offerId={LIVE_CHALLENGE_ORDER_OFFER_ID}
                      offerPaymentOptionId={LIVE_CHALLENGE_ORDER_OFFER_PAYMENT_OPTION_ID}
                      navigateUrl={optinNavigateUrl}
                      contactInfoFormButtonText="Register Now!"
                    />
                  )}
                </div>
              </Col>
              <Col xs="12" lg="10" className="video-col px-0 px-sm-3">
                <ScrollingVideo
                  video={{
                    url: {
                      default: 'https://www.youtube.com/watch?v=4MOuWDc5dGk',
                      wlcCustomer: 'https://www.youtube.com/watch?v=f699eFdDazA'
                    }[entry],
                    label: 'WLC LC - Landing Video'
                  }}
                  text={
                    <>
                      <h5 className="section-heading text-white">Join the Eat the Rainbow Challenge!</h5>
                    </>
                  }
                  button={{
                    scrollToSection: 'landing-cta-3',
                    text: 'Register Now!',
                    optin: true
                  }}
                  noFixed={noRegistration ? true : false}
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="landing-optimize"
          color="white"
          imgUrl="https://cdn.foodrevolution.org/eattherainbow/images/1450329680-square.jpg"
          imgDescription="woman in produce section of a supermarket"
          imgPosition="right"
          angle="both"
        >
          <h3 className="section-heading">Optimize your health with the power of the rainbow</h3>
          <p>
            If you take a trip down your produce aisle, you’ll see foods that come in every color of the rainbow. But
            these aren’t just for looks. Each color contains unique nutrients your body needs to thrive.
          </p>
          <h3 className="mt-0">Adding color to your plate could add years to your life</h3>
          <p>
            A varied, balanced diet with lots of colorful fruits and veggies gives your body the nutrition it needs to
            work optimally. If you miss out, your body will be more likely to suffer from disease, illness, and poor
            performance.
          </p>
        </SectionCL>

        <SectionCL
          id="landing-rescue"
          color="light-gray"
          imgUrl="https://cdn.foodrevolution.org/eattherainbow/images/1141197352-square.jpg"
          imgDescription="hands holding vegan, detox Buddha bowl with quinoa, micro greens, avocado, blood orange, broccoli, watermelon radish, alfalfa seed sprouts."
          imgPosition="left"
        >
          <h3 className="section-heading">Phytonutrients to the rescue!</h3>
          <p>
            Phytonutrients are the natural compounds in fruits and vegetables that give them color. Bonus points:
            phytonutrients protect us from disease.
          </p>
          <p>
            Studies show that most people are severely lacking in these health-boosting nutrients, contributing to a
            range of problems from unwanted weight gain to heart issues to cancer, poor gut health, and a struggling
            immune system.
          </p>
          <IconListCL>
            <>69% of Americans are falling short in green phytonutrients</>
            <>78% of Americans are falling short in red phytonutrients</>
            <>86% of Americans are falling short in white phytonutrients</>
            <>88% of Americans are falling short in purple and blue phytonutrients</>
            <>79% of Americans are falling short in yellow and orange phytonutrients</>
          </IconListCL>
          <p>
            <b>Fortunately, you don’t have to be one of them!</b>
          </p>
        </SectionCL>

        <SectionCL id="landing-kickoff" color="green" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">
                  That’s why it’s critical to Eat the Rainbow — and why we’re kicking off the Eat the Rainbow Challenge!
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="landing-challenge" color="white">
          <Container>
            <Row className="text-center mb-4">
              <Col>
                <h3 className="section-heading">How the Challenge Works</h3>
                <p className="balance-text">
                  Join thousands of people who are kicking off new, healthier habits in just five days!
                </p>
                <p className="balance-text">
                  Each day, we’ll focus on a different color and its health benefits, and you’ll get a fabulous recipe
                  that will add a vibrant pop of color to your plate.
                </p>
                <p className="balance-text">
                  Plus, you’ll get live recipe demonstrations, interviews with world-leading experts, and inspiration to
                  add mouthwatering color to your menu and vitality to your life!
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <ImageCardCL
                image="https://cdn.foodrevolution.org/wlc/challenge/images/1224030463.jpg"
                imageAlt="green ingredients for smoothie"
                type="vertical"
              >
                <h5 className="section-heading">May 16: Green — Metabolic Health</h5>
                <p>
                  It CAN be easy, eating green! Green foods are great sources of SO MANY important nutrients, including
                  iron and calcium, and have been shown to boost metabolism and help maintain a healthy weight. We’ll
                  include a stellar cooking demo with our own Rosie Mackay, MS, CNS, LDN, FRN’s Community Nutritionist,
                  and a Q&A with Ocean Robbins and Nichole Dandrea-Russert, RDN.
                </p>
                <p>Featured recipe: Walnut Pesto Angel Hair Pasta</p>
              </ImageCardCL>
              <ImageCardCL
                image="https://cdn.foodrevolution.org/wlc/challenge/images/665366284.jpg"
                imageAlt="legumes, beans, and grains"
                type="vertical"
              >
                <h5 className="section-heading">May 17: Brown and White — Gut Health</h5>
                <p>
                  White and brown foods might be less flashy than some other colors, but they pack a powerful
                  phytonutrient punch that can support your digestive health and keep your immune system in top shape.
                  Special guest: Dr. Alan Desmond, interviewed by Ocean Robbins.
                </p>
                <p>Featured recipe: White Bean and Mushroom Burger</p>
              </ImageCardCL>
              <ImageCardCL
                image="https://cdn.foodrevolution.org/wlc/challenge/images/503405167.jpg"
                imageAlt="red heart made from vegetables and fruit"
                type="vertical"
              >
                <h5 className="section-heading">May 18: Red, Orange, Yellow — Cardiovascular Health</h5>
                <p>
                  Heart disease is the number one cause of death worldwide, but the great news is that red foods are
                  some of our best allies when it comes to cardiovascular health. Treat yourself to a mini-chef course
                  with our cooking demo led by Rosie Mackay, MS, CNS, LDN!
                </p>
                <p>Featured recipe: Hearty Minestrone Butternut Soup</p>
              </ImageCardCL>
              <ImageCardCL
                image="https://cdn.foodrevolution.org/wlc/challenge/images/912113418.jpg"
                imageAlt="purple buddha bowl"
                type="vertical"
              >
                <h5 className="section-heading">May 19: Blue and Purple — Brain Health</h5>
                <p>
                  Ready to feel smarter, sharper, AND more productive? Science shows blue and purple veggies and fruits
                  can prevent cognitive decline — plus, they’re some of the tastiest foods on the planet! Special guest:
                  Ayesha Sherzai, MD, interviewed by Ocean Robbins.
                </p>
                <p>Featured recipe: Blueberry Ginger Smoothie</p>
              </ImageCardCL>
              <ImageCardCL
                image="https://cdn.foodrevolution.org/wlc/challenge/images/2150857924.jpg"
                imageAlt="rainbow colored salad in a bowl"
                type="vertical"
              >
                <h5 className="section-heading">May 20: The Rainbow — Overall Well-Being</h5>
                <p>
                  On our final day, we’ll discuss the rainbow of benefits you can experience when you eat a diet that's
                  rich in color and variety. You’ll enjoy another cooking demo with our team and learn about how you can
                  apply what you’re learning for a lifetime of good health.
                </p>
                <p>Featured recipe: Summer Rainbow Spring Rolls with Sweet Chili Mango Sauce</p>
              </ImageCardCL>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="landing-cta-2"
          color="purple"
          imgUrl="https://cdn.foodrevolution.org/eattherainbow/images/1452647782-square.jpg"
          imgDescription="couple cooking in the kitchen"
          imgPosition="right"
          className="text-center text-white"
        >
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading mb-2">Do your body good</h4>
                <h2 className="text-uppercase mt-0">Join the Eat The Rainbow Challenge</h2>
                <OptinTriggerContent />
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="landing-about" color="white">
          <Container>
            <Row>
              <Col>
                <ImageCardCL
                  image="https://cdn.foodrevolution.org/wlc/challenge/images/etr-guests-202505-002.jpg"
                  imageAlt="guest headshots on background"
                >
                  <h5 className="section-heading">Learn from Leading Health Experts</h5>
                  <p>Get the eye-opening facts about food that will jumpstart new healthy habits — for life!</p>
                  <p>
                    During the Challenge, you’ll learn from bestselling authors, researchers, and world-renowned food
                    experts as they share how to protect your health with the most colorful foods on the planet.
                  </p>
                </ImageCardCL>
                <ImageCardCL
                  image="https://cdn.foodrevolution.org/wlc/challenge/images/etr-demos-202505.jpg"
                  imageAlt="screenshot of cooking demo"
                >
                  <h5 className="section-heading">Recipe Demos</h5>
                  <p>
                    Join our brilliant Community Nutritionist, Rosie Mackay, MS, CNS, LDN, in the kitchen as she
                    demonstrates how to whip up colorful plant-based dishes to delight your taste buds.
                  </p>
                </ImageCardCL>
                <ImageCardCL
                  image="https://cdn.foodrevolution.org/wlc/challenge/images/etr-recipes-202505-002.jpg"
                  imageAlt="two recipe images side by side"
                >
                  <h5 className="section-heading">Daily Challenge</h5>
                  <p>
                    Each day, you’ll add a fresh new color (or two) to your plant-based menu by making a recipe starring
                    those colors! You’ll be doing your body a great big, colorful favor — with flavor!
                  </p>
                </ImageCardCL>
                <ImageCardCL
                  image="https://cdn.foodrevolution.org/eattherainbow/images/1369001065.jpg"
                  imageAlt="graphic of rainbow colored arms holding up a trophy"
                >
                  <h5 className="section-heading">Prizes</h5>
                  <p>
                    Post notes about your meal-making process or results (triumphs, hilarious mistakes, questions,
                    inspirations…) for a chance to win FABULOUS prizes. Of course, just by participating in the Eat the
                    Rainbow Challenge, you’ve already won!
                  </p>
                </ImageCardCL>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="landing-host"
          color="black"
          imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
          imgDescription="headshot of Ocean Robbins"
          imgPosition="right"
          className="text-white"
        >
          <h2 className="section-heading text-uppercase mb-2">Your Host</h2>
          <h3 className="section-heading">Ocean Robbins</h3>
          <p>
            Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx speaker,
            best-selling author, husband, father, gardener, and dancer. Born in a log cabin and raised on home-grown
            food, his work is rooted in his family’s health-conscious legacy. From founding the nonprofit YES! at age 16
            to leading a global food revolution movement, Ocean is on a mission to transform our food system, drive down
            the chronic disease epidemic, and mobilize people worldwide to be part of the solution for a healthier
            planet and population. His books include <i>31-Day Food Revolution</i> and <i>Powered By Plants.</i>
          </p>
        </SectionCL>

        <SectionCL id="landing-cohost" color="green" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading text-uppercase">Your Co-Hosts</h2>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        {cohosts.map((cohost, i) => (
          <SectionCL
            id={'landing-' + cohost.id}
            key={cohost.id}
            color="light-gray"
            imgUrl={cohost.image.url}
            imgPosition={i % 2 ? 'right' : 'left'}
            angleBottomDisplay={i < 2 ? 'lg-none' : 'default'}
            innerClass={i > 0 ? 'sectionCL-padding-lg' : 'default'}
          >
            <h3 className="section-heading">{cohost.name}</h3>
            <ReactMarkdown source={cohost.bio} />
          </SectionCL>
        ))}

        <SectionCL id="landing-guest" color="green" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading text-uppercase">Guest Experts</h2>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        {guests.map((guest, i) => (
          <SectionCL
            id={'landing-' + guest.id}
            key={guest.id}
            color="white"
            imgUrl={guest.image.url}
            imgPosition={i % 2 ? 'left' : 'right'}
            angleBottomDisplay={i < 1 ? 'lg-none' : 'default'}
            innerClass={i > 0 ? 'sectionCL-padding-lg' : 'default'}
          >
            <h3 className="section-heading">{guest.name}</h3>
            <ReactMarkdown source={guest.bio} />
          </SectionCL>
        ))}

        <SectionCL id="landing-faq" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">Frequently Asked Questions</h3>
                <FaqListCL faqlist={faqs} />
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="landing-cta-3"
          color="purple"
          imgUrl="https://cdn.foodrevolution.org/eattherainbow/images/1272443126-square.jpg"
          imgDescription="young woman receiving box of healthy foods"
          imgPosition="left"
          className="text-center text-white"
          order="last"
          angleBottomDisplay="none"
        >
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading mb-2">Do your body good</h4>
                <h2 className="text-uppercase mt-0 mb-4">Join the Eat The Rainbow Challenge</h2>
                <OptinTriggerContent />
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <OptinFormChallengeModal
        navigateUrl={optinNavigateUrl}
        optinFormProps={{
          apiUrl: SF_API_URL,
          orderApiUrl: ORDER_API_URL,
          siteId: SITE_ID,
          contactInfoFormHeader: (
            <div className="text-center">
              <p>
                Enter your email below to join the <b>Eat The Rainbow Challenge.</b>
              </p>
              {entry === 'wlcCustomer' && (
                <p>
                  <b>Note:</b> Please use the same email that you use to log into WHOLE Life Club. If you don’t use the
                  same email, a new account, with new login credentials, will be created for you.
                </p>
              )}
            </div>
          ),
          entry: entry,
          optinFormId: LIVE_CHALLENGE_OPTIN_FORM_ID,
          offerId: LIVE_CHALLENGE_ORDER_OFFER_ID,
          offerPaymentOptionID: LIVE_CHALLENGE_ORDER_OFFER_PAYMENT_OPTION_ID,
          contactInfoFormButtonText: 'Register Now!'
        }}
      />
      {!noRegistration && (
        <ExitModal
          exitDelay={30000}
          content={
            <p>
              Don’t miss out on the <b>Eat the Rainbow Challenge!</b> You’re just a step away from a vibrant and
              healthier you.
            </p>
          }
          image={{
            url: 'https://cdn.foodrevolution.org/eattherainbow/images/1208790364-exit.jpg',
            alt: 'healthy fresh rainbow colored fruits and vegetables in a row',
            width: 1000,
            height: 562
          }}
          positiveButtonText={<>Yes, sign me up!</>}
          positiveOptin
          negativeButtonText={<>No Thanks</>}
        />
      )}
      <Footer />
    </div>
  );
}
